import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { BCP47LangCode } from "./i18n.types";

/**
 * i18n configuration is sourced from the `i18n` field in package.json,
 * which acts as the single source of truth for supported languages.
 *
 * During the build step `generate:translations` aggregates all
 * `*.dictionary.<lang>.json` files into `__generated__/generated.<lang>.json`
 * bundles that are dynamically imported below.
 */
export const fallbackLng: BCP47LangCode = "en-US";

export const languages: BCP47LangCode[] = ["en-US", "es-MX", "fr-CA", "en-BR", "pt-BR"];

export const defaultNS = "translation";

/** Cookie name used to persist the chosen language across sessions. */
export const cookieName = "i18next";

let initPromise: Promise<void> | null = null;

export function initI18n() {
  if (i18next.isInitialized) {
    return Promise.resolve(i18next);
  }

  if (initPromise) {
    return initPromise.then(() => i18next);
  }

  initPromise = i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(
      resourcesToBackend(
        (language: string) =>
          import(
            /* webpackChunkName: "i18n-[request]" */
            `./__generated__/generated.${language}.json`
          ),
      ),
    )
    .init({
      supportedLngs: languages,
      fallbackLng,
      lng: undefined, // Let the detector decide
      fallbackNS: defaultNS,
      defaultNS,
      detection: {
        /** Detection order tuned for a client-side SPA (no URL path segment). */
        order: ["localStorage", "cookie", "navigator", "htmlTag"],
        caches: ["localStorage", "cookie"],
        lookupLocalStorage: "i18nextLng",
        lookupCookie: cookieName,
      },
      interpolation: {
        /** React already escapes values, so we disable i18next's escaping. */
        escapeValue: false,
      },
      react: {
        useSuspense: true,
      },
    })
    .then(() => undefined);

  return initPromise.then(() => i18next);
}
